/**
 * Utility for Interfacing with Bluetooth Thermal Printers (ESC/POS)
 * Targeted for EcoPrint and other generic 58mm POS printers.
 */

const PRINTER_SERVICE_UUID = '0000ff00-0000-1000-8000-00805f9b34fb'
const PRINTER_CHARACTERISTIC_UUID = '0000ff02-0000-1000-8000-00805f9b34fb'

// ESC/POS Command Constants
const ESC = 0x1B
const GS = 0x1D

export class ThermalPrinter {
  constructor() {
    this.isConnected = false
    this.lastDeviceId = localStorage.getItem('lastPrinterId')
  }

  /**
   * Check if there are already paired devices and try to connect
   */
  async autoConnect() {
    if (!navigator.bluetooth || !navigator.bluetooth.getDevices) return false

    try {
      const devices = await navigator.bluetooth.getDevices()
      if (devices.length > 0) {
        // Try to find the last used device
        const target =
          devices.find((d) => d.id === this.lastDeviceId) || devices[0]
        this.device = target
        return await this._internalConnect()
      }
    } catch (err) {
      console.warn('Auto-connect failed:', err)
    }
    return false
  }

  /**
   * Request device and connect via Web Bluetooth
   */
  async connect() {
    try {
      this.device = await navigator.bluetooth.requestDevice({
        filters: [
          { namePrefix: 'RPP' },
          { namePrefix: 'POS' },
          { namePrefix: 'MPT' },
          { namePrefix: 'MTP' },
          { namePrefix: 'PT-' },
          { namePrefix: 'Bluetooth Printer' },
          { namePrefix: 'EcoPrint' },
        ],
        optionalServices: [
          PRINTER_SERVICE_UUID,
          '000018f0-0000-1000-8000-00805f9b34fb',
          '49535343-fe7d-4ae5-8fa9-9fafd205e455',
          'e7e11001-5309-4f7d-8758-71e46114b038',
        ],
      })

      return await this._internalConnect()
    } catch (error) {
      console.error('Bluetooth Connection Failed:', error)
      throw error
    }
  }

  /**
   * Internal logic to establish GATT connection and find characteristic
   */
  async _internalConnect() {
    if (!this.device) return false

    try {
      this.server = await this.device.gatt.connect()

      const services = await this.server.getPrimaryServices()

      let foundCharacteristic = null

      for (const service of services) {
        try {
          const characteristics = await service.getCharacteristics()
          for (const char of characteristics) {
            if (char.properties.write || char.properties.writeWithoutResponse) {
              foundCharacteristic = char
              this.characteristic = char
              break
            }
          }
        } catch (e) {
          continue
        }
        if (foundCharacteristic) break
      }

      if (!foundCharacteristic) {
        throw new Error('No writable characteristic found for printing.')
      }

      this.isConnected = true
      localStorage.setItem('lastPrinterId', this.device.id)
      this.lastDeviceId = this.device.id
      this.device.addEventListener('gattserverdisconnected', () => {
        this.isConnected = false
        console.warn('Printer disconnected')
      })

      return true
    } catch (error) {
      console.error('Bluetooth Connection Failed:', error)
      throw error
    }
  }

  async disconnect() {
    if (this.device && this.device.gatt.connected) {
      await this.device.gatt.disconnect()
    }
    this.isConnected = false
  }

  /**
   * Helper to write bytes in chunks to stay within Bluetooth MTU limits
   */
  async write(data) {
    if (!this.characteristic) throw new Error('Printer not connected')

    // Increased chunk size for image stability vs speed
    const chunkSize = 120
    for (let i = 0; i < data.length; i += chunkSize) {
      const chunk = data.slice(i, i + chunkSize)
      await this.characteristic.writeValue(chunk)
    }
  }

  /**
   * Standard ESC/POS commands
   */
  async init() {
    await this.write(new Uint8Array([ESC, 0x40])) // ESC @ (Initialize)
  }

  async center() {
    await this.write(new Uint8Array([ESC, 0x61, 0x01])) // ESC a 1 (Center)
  }

  async left() {
    await this.write(new Uint8Array([ESC, 0x61, 0x00])) // ESC a 0 (Left)
  }

  async feed(lines = 3) {
    await this.write(new Uint8Array([ESC, 0x64, lines])) // ESC d n
  }

  async text(content) {
    const encoder = new TextEncoder()
    const bytes = encoder.encode(content + '\n')
    await this.write(bytes)
  }

  /**
   * Print QR Code using GS ( k commands
   * Compatible with most modern 58mm printers.
   */
  async printQR(data, size = 6) {
    // 1. Set model (Model 2)
    await this.write(new Uint8Array([GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x43, 0x32, 0x00]))
    
    // 2. Set size (1 to 16)
    await this.write(new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, size]))
    
    // 3. Set Error correction (Level M)
    await this.write(new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31]))

    // 4. Store data
    const dataBytes = new TextEncoder().encode(data)
    const pL = (dataBytes.length + 3) & 0xff
    const pH = (dataBytes.length + 3) >> 8
    
    const storeHeader = new Uint8Array([GS, 0x28, 0x6b, pL, pH, 0x31, 0x50, 0x30])
    await this.write(new Uint8Array([...storeHeader, ...dataBytes]))

    // 5. Print (Function 181)
    // Small delay before printing to ensure data is stored
    await new Promise(resolve => setTimeout(resolve, 100))
    await this.write(new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30]))
  }

  /**
   * Full method to print a single QR "item" with labels
   */
  async printQrItem(title, subtitle, qrValue) {
    await this.init()
    await this.center()
    
    // Title (Bold)
    await this.write(new Uint8Array([ESC, 0x45, 0x01])) // ESC E 1 (Bold on)
    await this.text(title)
    await this.write(new Uint8Array([ESC, 0x45, 0x00])) // ESC E 0 (Bold off)
    
    // Subtitle
    await this.text(subtitle)
    
    // Spacing
    await this.feed(1)
    
    // QR Code
    await this.printQR(qrValue)
    
    // Footer spacing
    // Footer spacing
    await this.feed(4)
  }

  /**
   * Print Image from Canvas
   * Uses GS v 0 (Raster bit image)
   */
  async printImage(canvas) {
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const pixels = imgData.data

    // Width in bytes (must be multiple of 8)
    const widthBytes = Math.ceil(canvas.width / 8)
    const height = canvas.height

    const bitData = new Uint8Array(widthBytes * height)

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const idx = (y * canvas.width + x) * 4
        // Calculate grayscale: (R+G+B)/3
        const r = pixels[idx]
        const g = pixels[idx + 1]
        const b = pixels[idx + 2]
        const alpha = pixels[idx + 3]

        // Thresholding: Black if alpha is high and brightness is low
        const isBlack = alpha > 128 && (r + g + b) / 3 < 160

        if (isBlack) {
          const byteIdx = y * widthBytes + Math.floor(x / 8)
          const bitPos = 7 - (x % 8)
          bitData[byteIdx] |= 1 << bitPos
        }
      }
    }

    // Command: GS v 0 m xL xH yL yH
    const xL = widthBytes & 0xff
    const xH = (widthBytes >> 8) & 0xff
    const yL = height & 0xff
    const yH = (height >> 8) & 0xff

    const header = new Uint8Array([GS, 0x76, 0x30, 0x00, xL, xH, yL, yH])
    await this.write(header)
    
    // Write bit data in slightly larger chunks for raster images
    const bitChunkSize = 900 
    for (let i = 0; i < bitData.length; i += bitChunkSize) {
      await this.write(bitData.slice(i, i + bitChunkSize))
    }
  }

  /**
   * Print a combination of text and an image (QR)
   */
  async printQrAsImage(title, subtitle, canvas) {
    await this.init()
    await this.center()
    
    // Title (Bold)
    await this.write(new Uint8Array([ESC, 0x45, 0x01]))
    await this.text(title)
    await this.write(new Uint8Array([ESC, 0x45, 0x00]))
    
    // Subtitle
    await this.text(subtitle)
    await this.feed(1)
    
    // Image (The QR)
    if (canvas) {
      await this.printImage(canvas)
    }
    
    await this.feed(4)
  }
}

export const thermalPrinter = new ThermalPrinter()
